import { useState } from 'react';
import { signIn, signUp, getLearner, supabase } from '../services/supabaseClient.js';

// Public account creation goes through /registration (the full consent-
// capturing flow with Terms/Privacy/SMS). /signin is sign-in only. The
// /signin?signup=1 QA bypass keeps an inline email+password signup so admins
// can create test accounts via the /admin "Open Create Account" button.
const signupBypass = typeof window !== 'undefined'
  && new URLSearchParams(window.location.search).get('signup') === '1';

export default function AuthScreen({ onAuth }) {
  const [mode,     setMode]     = useState(signupBypass ? 'signup' : 'signin');
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);
  // Forgot-password flow — inline expand within the same card.
  const [view,         setView]         = useState('signin'); // 'signin' | 'forgot' | 'forgot_sent'
  const [resetEmail,   setResetEmail]   = useState('');
  const [resetError,   setResetError]   = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  const handleReset = async () => {
    setResetError('');
    if (!resetEmail.trim()) { setResetError('Enter your email.'); return; }
    setResetLoading(true);
    try {
      const { error: err } = await supabase.auth.resetPasswordForEmail(resetEmail.trim(), {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (err) throw err;
      setView('forgot_sent');
    } catch (err) {
      setResetError(err.message || 'Could not send the reset link. Try again.');
    }
    setResetLoading(false);
  };

  const handleSubmit = async () => {
    setError('');
    if (!email.trim() || !password.trim()) { setError('Email and password are required.'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    setLoading(true);
    try {
      if (mode === 'signup') {
        // Name is collected in onboarding (first_name + last_name → concatenated
        // into learners.name by completeOnboarding). No reason to ask twice.
        const user    = await signUp(email.trim(), password, null);
        // Fetch the freshly-created learner row so the onboarding gate sees a
        // valid created_at and onboarding_completed_at=null → routes to onboarding.
        const learner = await getLearner(user.id);
        onAuth({
          id:                      user.id,
          name:                    learner?.name || null,
          first_name:              learner?.first_name || null,
          last_name:               learner?.last_name  || null,
          email:                   email.trim(),
          onboarding_completed_at: learner?.onboarding_completed_at || null,
          created_at:              learner?.created_at              || new Date().toISOString(),
        });
      } else {
        const user    = await signIn(email.trim(), password);
        const learner = await getLearner(user.id);
        onAuth({
          id:            user.id,
          name:          learner?.name          || email.trim(),
          first_name:    learner?.first_name    || null,
          last_name:     learner?.last_name     || null,
          email:         email.trim(),
          experience:    learner?.experience    || null,
          concern:       learner?.concern       || null,
          learning_mode: learner?.learning_mode || null,
          target_date:   learner?.target_date   || null,
          current_track: learner?.current_track || null,
          cert_path:               Array.isArray(learner?.cert_path) ? learner.cert_path : null,
          confidence_baseline:     learner?.confidence_baseline     || null,
          study_hours:             learner?.study_hours             || null,
          // App.jsx's onboarding gate reads both — onboarding_completed_at and
          // created_at (24h freshness check). Pre-existing accounts that somehow
          // missed the grandfather migration won't be sent to onboarding.
          onboarding_completed_at: learner?.onboarding_completed_at || null,
          created_at:              learner?.created_at              || null,
          subscription_status:     learner?.subscription_status     || 'none',
          trial_end:               learner?.trial_end               || null,
          stripe_customer_id:      learner?.stripe_customer_id      || null,
        });
      }
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  const inputStyle = {
    padding: '12px 14px', borderRadius: 10,
    background: 'var(--bg-elevated)',
    border: '1px solid var(--border-medium)',
    color: 'var(--text-primary)', fontSize: 14, outline: 'none',
    fontFamily: 'inherit', width: '100%',
    boxSizing: 'border-box',
  };

  return (
    <div style={{
      minHeight: '100vh', width: '100vw',
      background: 'var(--bg-primary)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: "'Inter','Segoe UI',sans-serif",
    }}>
      <div style={{
        width: '100%', maxWidth: 440, margin: '0 24px',
        background: 'var(--bg-card)',
        border: '1px solid var(--border-soft)',
        borderRadius: 24, overflow: 'hidden', padding: '44px 40px',
        boxShadow: 'var(--shadow-card)',
      }}>

        {/* Logo on a full-bleed black banner — matches the registration screen */}
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#1a1a24', margin: '-44px -40px 28px', padding: '26px 20px' }}>
          <img src="/logo.jpg" alt="AdaptOS" style={{ height: 150, width: 'auto', objectFit: 'contain' }}/>
        </div>

        <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 28, textAlign: 'center' }}>
          {mode === 'signin' ? 'Welcome back. Your study session awaits.' : 'Start your certification journey today.'}
        </p>

        {view === 'signin' ? (
        <>
        {/* Tabs */}
        {signupBypass && (
          <div style={{ display: 'flex', background: 'var(--bg-elevated)', borderRadius: 10, padding: 3, marginBottom: 28, gap: 3 }}>
            {['signin','signup'].map(m => (
              <button key={m} onClick={() => { setMode(m); setError(''); }} style={{
                flex: 1, padding: '9px 0', borderRadius: 8, border: 'none',
                background: mode === m ? 'var(--bg-elevated)' : 'transparent',
                color: mode === m ? 'var(--text-primary)' : 'var(--text-muted)',
                fontSize: 13, fontWeight: 600, cursor: 'pointer',
                fontFamily: 'inherit', transition: 'all 0.2s',
                boxShadow: mode === m ? '0 1px 4px rgba(0,0,0,0.2)' : 'none',
              }}>
                {m === 'signin' ? 'Sign In' : 'Create Account'}
              </button>
            ))}
          </div>
        )}

        {/* Fields — name is collected in onboarding (first/last), so we only
            ask for email + password here. */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 12 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8 }}>Email</label>
            <input type="email" value={email} placeholder="you@example.com"
              onChange={e => setEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSubmit()}
              style={inputStyle}/>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8 }}>Password</label>
            <input type="password" value={password} placeholder="••••••••"
              onChange={e => setPassword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSubmit()}
              style={inputStyle}/>
          </div>
        </div>

        {mode === 'signin' && (
          <p style={{ textAlign: 'right', margin: '0 0 16px' }}>
            <span onClick={() => { setView('forgot'); setResetEmail(email); setResetError(''); }}
              style={{ fontSize: 12.5, color: 'var(--accent-primary)', cursor: 'pointer', fontWeight: 600 }}>Forgot password?</span>
          </p>
        )}

        {error && (
          <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgba(248,113,113,0.10)', border: '1px solid rgba(248,113,113,0.25)', color: 'var(--progress-weak)', fontSize: 13, marginBottom: 16, lineHeight: 1.5 }}>
            {error}
          </div>
        )}

        <button onClick={handleSubmit} disabled={loading} style={{
          width: '100%', padding: '13px 24px', borderRadius: 12,
          background: 'linear-gradient(135deg, var(--accent-hover), var(--accent-primary))',
          border: 'none', color: 'var(--accent-text)',
          fontSize: 15, fontWeight: 700, cursor: loading ? 'default' : 'pointer',
          fontFamily: 'inherit', opacity: loading ? 0.7 : 1,
          transition: 'all 0.2s', marginBottom: 20,
          letterSpacing: 0.2,
        }}>
          {loading ? 'Please wait...' : mode === 'signin' ? 'Sign In' : 'Create Account'}
        </button>

        {signupBypass && (
          <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--text-muted)' }}>
            {mode === 'signin' ? "Don't have an account? " : 'Already have an account? '}
            <span onClick={() => { setMode(mode === 'signin' ? 'signup' : 'signin'); setError(''); }}
              style={{ color: 'var(--accent-primary)', cursor: 'pointer', fontWeight: 600 }}>
              {mode === 'signin' ? 'Create one' : 'Sign in'}
            </span>
          </p>
        )}

        {/* New users → full registration flow (consent capture), as an amber CTA. */}
        <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--text-muted)', margin: '4px 0 10px' }}>
          New to AdaptOS?
        </p>
        <a href="/registration" style={{
          display: 'block', width: '100%', boxSizing: 'border-box', textAlign: 'center',
          padding: '13px 24px', borderRadius: 12,
          background: 'linear-gradient(135deg, #f59e0b, #d97706)',
          color: '#ffffff', fontSize: 15, fontWeight: 700, letterSpacing: 0.2,
          textDecoration: 'none',
        }}>
          Create Account
        </a>
        </>
        ) : view === 'forgot' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', textAlign: 'center', margin: 0, lineHeight: 1.5 }}>
              Enter your email and we&rsquo;ll send a reset link.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8 }}>Email</label>
              <input type="email" value={resetEmail} placeholder="you@example.com"
                onChange={e => setResetEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleReset()}
                style={inputStyle}/>
            </div>
            {resetError && (
              <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.25)', color: 'var(--brand-coral)', fontSize: 13, lineHeight: 1.5 }}>{resetError}</div>
            )}
            <button onClick={handleReset} disabled={resetLoading} style={{
              width: '100%', padding: '13px 24px', borderRadius: 12, background: 'var(--brand-amber)', border: 'none',
              color: '#fff', fontSize: 15, fontWeight: 700, cursor: resetLoading ? 'default' : 'pointer', fontFamily: 'inherit', opacity: resetLoading ? 0.7 : 1,
            }}>{resetLoading ? 'Sending…' : 'Send Reset Link'}</button>
            <p style={{ textAlign: 'center', fontSize: 13 }}>
              <span onClick={() => { setView('signin'); setResetError(''); }} style={{ color: 'var(--accent-primary)', cursor: 'pointer', fontWeight: 600 }}>← Back to sign in</span>
            </p>
          </div>
        ) : (
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontSize: 40, margin: '0 0 10px' }}>✉️</p>
            <p style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 6px' }}>Check your email</p>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 22px', lineHeight: 1.55 }}>
              A reset link is on its way to <strong>{resetEmail}</strong>. It expires shortly, so use it soon.
            </p>
            <p style={{ fontSize: 13 }}>
              <span onClick={() => { setView('signin'); }} style={{ color: 'var(--accent-primary)', cursor: 'pointer', fontWeight: 600 }}>← Back to sign in</span>
            </p>
          </div>
        )}
      </div>
      <style>{`@keyframes orbP{0%,100%{transform:scale(1);opacity:.8}50%{transform:scale(1.1);opacity:1}}`}</style>
    </div>
  );
}
